/**
 * Automatické testy fee běhů (fáze 6) — invarianty nad management a success fee.
 * Čisté funkce (Dec), testuje vitest. Mock je volá při vytvoření běhu, UI zobrazuje
 * výsledky (stejný vzor jako invarianty přepočtu jednotek, `invariants.ts`).
 */

import { AutoTestResult } from '../api/types'
import { Dec } from './dec'
import { reconstructedAmount } from './preview'

/** Tolerance peněžní kontroly (haléře po zaokrouhlení). */
const MONEY_TOL = 0.01
/** Tolerance jednotkové kontroly. */
const UNIT_TOL = 0.000001

function sum(values: number[]): Dec {
  return values.reduce<Dec>((acc, v) => acc.add(Dec.of(v)), Dec.ZERO)
}
function abs(a: number, b: number): number {
  return Dec.of(a).sub(Dec.of(b)).abs().toNumber()
}

// ---- Management fee ----

export interface MgmtFeeCheckInput {
  totalFee: number
  lines: Array<{ clientId: string; clientName: string; holdingValue: number; fee: number }>
  byDistributor: Array<{ totalFee: number }>
  fundReductions: Array<{
    fundKey: string
    navBefore: number
    navAfter: number
    mfAmount: number
    unitPriceAfter: number
  }>
}

export function runMgmtFeeInvariants(input: MgmtFeeCheckInput): AutoTestResult[] {
  const lineSum = sum(input.lines.map((l) => l.fee)).toNumber()
  const distSum = sum(input.byDistributor.map((d) => d.totalFee)).toNumber()
  const navSum = sum(input.fundReductions.map((r) => r.mfAmount)).toNumber()

  // 1. Σ fee řádků = celkové fee
  const t1 = abs(lineSum, input.totalFee) <= MONEY_TOL
  // 2. Σ fee per distributor = celkové fee (report per distributor sedí na celek)
  const t2 = abs(distSum, input.totalFee) <= MONEY_TOL
  // 3. Σ snížení NAV fondů = celkové fee (nepřímé stržení pokrývá celý MF)
  const t3 = abs(navSum, input.totalFee) <= MONEY_TOL
  // 4. NAV po každém fondu = před − MF a stále kladné; cena > 0
  let navOk = true
  let navDetail = 'NAV fondů klesla o MF a zůstala kladná'
  for (const r of input.fundReductions) {
    const expected = Dec.of(r.navBefore).sub(Dec.of(r.mfAmount)).toNumber()
    if (abs(expected, r.navAfter) > MONEY_TOL || !(r.navAfter > 0) || !(r.unitPriceAfter > 0)) {
      navOk = false
      navDetail = `${r.fundKey}: NAV/cena po MF nesedí nebo není kladná`
      break
    }
  }
  // 5. žádný klient 2×
  const ids = input.lines.map((l) => l.clientId)
  const dupOk = new Set(ids).size === ids.length

  return [
    { name: 'Σ fee řádků = celkem', passed: t1, detail: `${lineSum} vs ${input.totalFee}` },
    { name: 'Σ fee per distributor = celkem', passed: t2, detail: `${distSum} vs ${input.totalFee}` },
    { name: 'Σ snížení NAV fondů = celkem', passed: t3, detail: `${navSum} vs ${input.totalFee}` },
    { name: 'NAV fondu po MF kladná a konzistentní', passed: navOk, detail: navDetail },
    {
      name: 'žádný klient 2×',
      passed: dupOk,
      detail: dupOk ? `${ids.length} klientů` : 'klient je v běhu vícekrát',
    },
  ]
}

// ---- Success fee ----

export interface SuccessFeeCheckInput {
  totalFee: number
  totalFeeUnits: number
  totalDistribution: number
  yearEndPrice: number
  fundTotalUnits: number
  distributionRate: number
  lines: Array<{
    clientId: string
    clientName: string
    units: number
    invested: number
    value: number
    profit: number
    fee: number
    feeUnits: number
    rate: number
    distribution: number
  }>
}

export function runSuccessFeeInvariants(input: SuccessFeeCheckInput): AutoTestResult[] {
  const feeSum = sum(input.lines.map((l) => l.fee)).toNumber()
  const unitsSum = sum(input.lines.map((l) => l.feeUnits)).toNumber()
  const distSum = sum(input.lines.map((l) => l.distribution)).toNumber()

  // 1. Σ fee = totalFee; 2. Σ feeUnits = totalFeeUnits; 3. Σ distribuce = totalDistribution
  const t1 = abs(feeSum, input.totalFee) <= MONEY_TOL
  const t2 = abs(unitsSum, input.totalFeeUnits) <= UNIT_TOL
  const t3 = abs(distSum, input.totalDistribution) <= MONEY_TOL

  // 4. per řádek: fee = max(0, hodnota − invested) × sazba; distribuce = fee × distributionRate
  let lineOk = true
  let lineDetail = 'fee = zisk × sazba, distribuce = fee × podíl'
  for (const l of input.lines) {
    const profit = Math.max(0, Dec.of(l.value).sub(Dec.of(l.invested)).toNumber())
    const expFee = reconstructedAmount(profit, l.rate) // round(profit × rate, 4)
    const expDist = reconstructedAmount(l.fee, input.distributionRate)
    // stržené jednotky × cena ≈ fee
    const feeFromUnits = reconstructedAmount(l.feeUnits, input.yearEndPrice)
    if (
      abs(profit, l.profit) > MONEY_TOL ||
      abs(expFee, l.fee) > MONEY_TOL ||
      abs(expDist, l.distribution) > MONEY_TOL ||
      abs(feeFromUnits, l.fee) > MONEY_TOL
    ) {
      lineOk = false
      lineDetail = `${l.clientName}: fee/jednotky/distribuce nesedí na vzorec`
      break
    }
  }

  // 5. Σ stržených jednotek ≤ total_units fondu (nestrhne víc, než je)
  const unitsWithin = Dec.of(input.totalFeeUnits).cmp(Dec.of(input.fundTotalUnits)) <= 0
  // 6. cena konce roku > 0
  const priceOk = input.yearEndPrice > 0

  return [
    { name: 'Σ fee řádků = celkem', passed: t1, detail: `${feeSum} vs ${input.totalFee}` },
    { name: 'Σ stržených jednotek = celkem', passed: t2, detail: `${unitsSum} vs ${input.totalFeeUnits}` },
    { name: 'Σ distribuce = celkem', passed: t3, detail: `${distSum} vs ${input.totalDistribution}` },
    { name: 'fee = zisk × sazba (per řádek)', passed: lineOk, detail: lineDetail },
    {
      name: 'stržené jednotky ≤ total_units fondu',
      passed: unitsWithin,
      detail: `${input.totalFeeUnits} ≤ ${input.fundTotalUnits}`,
    },
    { name: 'cena konce roku > 0', passed: priceOk, detail: `${input.yearEndPrice}` },
  ]
}

/** Všechny invarianty prošly (pro verdikt / gating). */
export function allFeeChecksPassed(tests: AutoTestResult[]): boolean {
  return tests.length > 0 && tests.every((t) => t.passed)
}
